import React, {useEffect, useMemo, useState} from "react";
import {useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {Button, PanelHeader, PanelHeaderBack, Spinner} from "@vkontakte/vkui";
import {fetchActiveMissions, claimMissionReward} from "../../services/missions";
import {useSnackbar} from "../../context/SnackbarContext";
import BonusStore from "../../store/bonus";
import GameStore from "../../store/game";
import {Icon28TargetOutline} from "@vkontakte/icons";
import {getSubscriptionStatus} from "../../services/subscription";
import {SUBSCRIBE_MISSIONS_MODAL} from "../../routing/routing";
import {numberWordSuffix} from "../../utils/word";
import {getTournamentTimeBadgeClasses} from "../../utils/badges";
import {catalogPreviewMissions, isCatalogPreview} from "../../utils/catalogPreview";

const BADGE_CLASSES = "arcade-badge";
const BUTTON_CLASSES = "app-btn-primary";
const TIME_TARGET_METRIC = "time_played_seconds";
const SUBSCRIPTION_MODAL_SHOWN_KEY = "subscriptionModalShownAfterMission";

const hintLabels = {
    time_boost: "«Добавить время»",
    word_contour: "«Показать контур»"
};

// Форматирует оставшееся время в человекочитаемый формат
function formatTimeLeft(seconds) {
    if (seconds <= 0) {
        return "истёкло";
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;

    if (hours > 0) {
        return `${hours}ч ${minutes.toString().padStart(2, "0")}м`;
    }
    if (minutes > 0) {
        return `${minutes}м ${remainder.toString().padStart(2, "0")}с`;
    }
    return `${remainder}с`;
}

// Нормализует объект миссии, чтобы избежать undefined
function normalizeMission(mission) {
    return {
        ...mission,
        title: mission.title ?? "",
        description: mission.description ?? "",
        // Оставляем секунды как есть от сервера (в московском времени).
        timeLeftSeconds: Math.max(Math.floor(mission.timeLeftSeconds ?? 0), 0),
        progress: Math.max(mission.progress ?? 0, 0),
        targetValue: Math.max(mission.targetValue ?? 0, 0),
        targetMetric: mission.targetMetric ?? "",
        rewardPreview: mission.rewardPreview || {}
    };
}

// Возвращает целое число минут для прогресса по времени
function toWholeMinutes(value) {
    return Math.floor(value / 60);
}

// Форматирует текст прогресса в зависимости от метрики
function getMissionProgressText(mission) {
    if (mission.targetMetric !== TIME_TARGET_METRIC) {
        return `${mission.progress}/${mission.targetValue}`;
    }
    const progressMinutes = toWholeMinutes(mission.progress);
    const targetMinutes = toWholeMinutes(mission.targetValue);
    return `${progressMinutes}/${targetMinutes} мин`;
}

// Уменьшает таймер миссии и отмечает, если она истекла
function updateTime(mission) {
    if (mission.timeLeftSeconds <= 0) {
        if (mission.status === "expired") {
            return mission;
        }
        // Не скрываем выполненные/полученные миссии, даже если время истекло.
        if (mission.status === "completed" || mission.status === "claimed") {
            return {...mission, timeLeftSeconds: 0};
        }
        return {...mission, timeLeftSeconds: 0, status: "expired"};
    }
    return {...mission, timeLeftSeconds: mission.timeLeftSeconds - 1};
}

// Возвращает общий таймер для группы миссий, чтобы не дублировать счётчики в карточках.
function getGroupTimeLeftSeconds(missions) {
    return missions.reduce((maxValue, mission) => Math.max(maxValue, mission.timeLeftSeconds ?? 0), 0);
}

export default function MissionPanel() {
    const catalogPreview = isCatalogPreview();
    const [missions, setMissions] = useState(catalogPreview ? catalogPreviewMissions : {daily: [], weekly: []});
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();
    const handleBackButton = () => (isFirstPage ? routeNavigator.push("/") : routeNavigator.back());
    const [loading, setLoading] = useState(!catalogPreview);
    const [error, setError] = useState("");
    const [claiming, setClaiming] = useState({});
    const {showErrorSnackbar, showSuccessSnackbar} = useSnackbar();

    const loadMissions = () => {
        if (catalogPreview) {
            setMissions(catalogPreviewMissions);
            setLoading(false);
            return;
        }
        setLoading(true);
        fetchActiveMissions()
            .then(resp => {
                const payload = resp.data?.response || {daily: [], weekly: []};
                setMissions({
                    daily: (payload.daily || []).map(normalizeMission),
                    weekly: (payload.weekly || []).map(normalizeMission)
                });
                setError("");
            })
            .catch(err => {
                console.error("missions load error", err);
                setError("Не удалось загрузить миссии");
                showErrorSnackbar("Не удалось загрузить миссии");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        // Режим каталога использует локальные миссии и не запускает сетевое обновление.
        if (catalogPreview) {
            return undefined;
        }
        loadMissions();
        const poll = setInterval(loadMissions, 60000);
        return () => clearInterval(poll);
    }, []);

    useEffect(() => {
        const tick = setInterval(() => {
            setMissions(prev => ({
                daily: prev.daily.map(updateTime),
                weekly: prev.weekly.map(updateTime)
            }));
        }, 1000);
        return () => clearInterval(tick);
    }, []);

    const handleClaim = (missionId) => {
        // Демонстрационная награда меняет только локальное состояние экрана.
        if (catalogPreview) {
            setMissions(prev => ({
                daily: prev.daily.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m),
                weekly: prev.weekly.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m)
            }));
            showSuccessSnackbar("Награда за миссию получена");
            return;
        }
        setClaiming(prev => ({...prev, [missionId]: true}));

        claimMissionReward(missionId)
            .then(resp => {
                const payload = resp.data?.response || {coins: 0, playerHintBalances: {}};
                BonusStore.setCoins(payload.coins ?? BonusStore.coins);
                if (payload.playerHintBalances) {
                    GameStore.setPlayerHintBalances(payload.playerHintBalances);
                }
                setMissions(prev => ({
                    daily: prev.daily.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m),
                    weekly: prev.weekly.map(m => m.id === missionId ? {...m, status: "claimed", progress: m.targetValue} : m)
                }));
                showSuccessSnackbar("Награда за миссию получена");

                // Показываем окно подписки после получения награды (если ещё не подписан и не показывали в сессии)
                if (sessionStorage.getItem(SUBSCRIPTION_MODAL_SHOWN_KEY) !== "1") {
                    getSubscriptionStatus().then(response => {
                        if (!response?.data?.response?.subscribed) {
                            sessionStorage.setItem(SUBSCRIPTION_MODAL_SHOWN_KEY, "1");
                            routeNavigator.showModal(SUBSCRIBE_MISSIONS_MODAL);
                        }
                    }).catch(err => {
                        console.error("Не удалось проверить статус подписки:", err);
                    });
                }
            })
            .catch(err => {
                console.error("mission claim error", err);
                showErrorSnackbar("Не удалось получить награду");
            })
            .finally(() => {
                setClaiming(prev => ({...prev, [missionId]: false}));
            });
    };

    return (
        <div className="app-screen">
            <PanelHeader
                before={<PanelHeaderBack className="[&>svg]:p-0" onClick={handleBackButton}/>}
                separator={null}
            >
                Миссии
            </PanelHeader>

            {/* Контейнер повторяет ограничение ширины max-w-lg, как на других панелях */}
            <div className="app-screen__content items-center">
                <div className="w-full">
                    <MissionContent
                        missions={missions}
                        loading={loading}
                        error={error}
                        claiming={claiming}
                        onClaim={handleClaim}
                    />
                </div>
            </div>
        </div>
    );
}

function MissionContent({missions, loading, error, claiming, onClaim}) {
    return (
        <div className="arcade-panel w-full text-gray-800 p-5 space-y-6">
            <div className="flex items-center gap-3">
                <div className="arcade-icon-frame w-12 h-12 flex-shrink-0 flex items-center justify-center">
                    <Icon28TargetOutline/>
                </div>
                <div className={"text-gray-800"}>
                    <p className="arcade-title text-base font-black">Миссии</p>
                    <p className="text-sm">Следите за прогрессом по ежедневным и недельным целям</p>
                </div>
            </div>

            <MissionColumn
                title="Дневные миссии"
                groupType="daily"
                missions={missions.daily}
                loading={loading}
                statusMessage={error && !missions.daily.length ? error : ""}
                onClaim={onClaim}
                claiming={claiming}
            />
            <MissionColumn
                title="Недельные миссии"
                groupType="weekly"
                missions={missions.weekly}
                loading={loading}
                statusMessage={error && !missions.weekly.length ? error : ""}
                onClaim={onClaim}
                claiming={claiming}
            />
        </div>
    );
}

function MissionColumn({title, groupType, missions, loading, statusMessage, onClaim, claiming}) {
    const visibleMissions = useMemo(() => missions.filter(m => m.status !== "expired"), [missions]);
    const groupTimeLeftSeconds = useMemo(() => getGroupTimeLeftSeconds(visibleMissions), [visibleMissions]);
    const groupBadgeClasses = getTournamentTimeBadgeClasses(groupType);
    // Поднимаем выполненные миссии вверх, чтобы акцентировать готовые награды.
    const orderedMissions = useMemo(() => [...visibleMissions].sort((a, b) => {
        const score = (status) => (status === "completed" ? 0 : status === "claimed" ? 2 : 1);
        return score(a.status) - score(b.status);
    }), [visibleMissions]);

    return (
        <div className="app-list-card arcade-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className="text-sm font-black text-gray-800">{title}</p>
                    <p className="text-xs text-gray-700">
                        {visibleMissions.length
                            ? `${visibleMissions.length} ${numberWordSuffix(visibleMissions.length, ["карточка", "карточки", "карточек"])}`
                            : "в ожидании"}
                    </p>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                    {visibleMissions.length > 0 && (
                        <span className={groupBadgeClasses}>
                            {formatTimeLeft(groupTimeLeftSeconds)}
                        </span>
                    )}
                </div>

            </div>

            {statusMessage && (
                <div className="text-xs text-brand-danger">{statusMessage}</div>
            )}

            {loading && visibleMissions.length === 0 ? (
                <div className="flex justify-center py-6">
                    <Spinner size="small"/>
                </div>
            ) : visibleMissions.length === 0 ? (
                <div className="arcade-card p-4 text-xs text-gray-700">
                    Пока нет миссий. Загляните позже — цели обновляются регулярно.
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {orderedMissions.map(mission => (
                        <MissionCard
                            key={`${mission.id}-${mission.status}`}
                            mission={mission}
                            onClaim={onClaim}
                            claiming={Boolean(claiming[mission.id])}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function MissionCard({mission, onClaim, claiming}) {
    const progressPercent = mission.targetValue > 0 ? Math.min(100, Math.round((mission.progress / mission.targetValue) * 100)) : 0;
    const rewardParts = [];
    const progressText = getMissionProgressText(mission);
    const isCompleted = mission.status === "completed";
    const isClaimed = mission.status === "claimed";

    if (mission.rewardPreview?.coins) {
        rewardParts.push(`${mission.rewardPreview.coins} монет`);
    }

    if (mission.rewardPreview?.hint_tokens) {
        Object.entries(mission.rewardPreview.hint_tokens).forEach(([type, count]) => {
            const label = hintLabels[type] || type;
            rewardParts.push(`+${count} ${label}`);
        });
    }

    return (
        <div className="app-list-card arcade-card p-4 flex flex-col gap-3">
            <div>
                <p className="text-base font-black text-gray-900">{mission.title}</p>
                <p className="text-xs text-gray-700">{mission.description}</p>
            </div>

            <div className="space-y-2">
                <div className="arcade-progress w-full h-2.5">
                    <div
                        className="arcade-progress__bar"
                        style={{width: `${progressPercent}%`}}
                    />
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-gray-800">{progressText}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-gray-700">
                {rewardParts.length > 0 ? rewardParts.map(part => (
                    <span key={part} className="arcade-badge">{part}</span>
                )) : <span className="text-gray-700">Награда откроется после выполнения</span>}
                {isClaimed && (
                    <span className={BADGE_CLASSES}>
                        Награда получена
                    </span>
                )}
            </div>

            {isClaimed ? null : isCompleted ? (
                <Button
                    size="s"
                    mode="primary"
                    stretched
                    className={BUTTON_CLASSES}
                    disabled={claiming}
                    onClick={() => onClaim(mission.id)}
                >
                    {claiming ? "Забираем..." : "Забрать награду"}
                </Button>
            ) : (
                <div className="text-xs text-gray-700">
                    В процессе выполнения — продолжайте играть
                </div>
            )}
        </div>
    );
}
